"use client";

import { useState, useEffect } from "react";
import { useIMQA, DEFAULT_IMQA_CONFIG } from "@/components/imqa-provider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";

interface AdvancedConfig {
  // Core settings
  collectorUrl: string;
  serviceName: string;
  serviceVersion: string;
  serviceKey: string;
  serviceNamespace: string;
  authorizationToken: string;
  deploymentEnvironment: string;
  
  // Sampling & Performance
  samplingProbability: number;
  advancedNetworkCapture: boolean;
  captureMetric: boolean;
  webVitals: boolean;
  
  // Console & Debug
  consoleCapture: boolean;
  debug: boolean;
  
  // Session Replay
  disableReplay: boolean;
  sampling: number;
  recordCanvas: boolean;
  maskAllInputs: boolean;
  maskAllText: boolean;
  blockClass: string;
  ignoreClass: string;
  maskClass: string;
  
  // Storage
  sessionStorage: 'cookie' | 'localStorage';
  
  // Network
  websocket: boolean;
  socketio: boolean;
  disablePreflight: boolean;
  exporterProtocol: 'json' | 'proto';
  tracePropagationTargets: string;
  ignoreUrls: string;
  
  // Screen Name
  screenNameType: 'routeOnly' | 'titleOnly' | 'full';
  urlWithSearchParams: boolean;
  
  // Instrumentations
  instrumentations: {
    console: boolean;
    document: boolean;
    errors: boolean;
    fetch: boolean;
    interactions: boolean;
    longtask: boolean;
    visibility: boolean;
    connectivity: boolean;
    postload: boolean;
    socketio: boolean;
    websocket: boolean;
    webvitals: boolean;
    xhr: boolean;
  };
  
  // Custom attributes
  defaultAttributes: string; // JSON string
}

const defaultConfig: AdvancedConfig = {
  // Core settings - empty values, will show placeholders
  collectorUrl: '',
  serviceName: '',
  serviceVersion: '',
  serviceKey: '',
  serviceNamespace: '',
  authorizationToken: '',
  deploymentEnvironment: 'development',
  
  // Sampling & Performance
  samplingProbability: 1,
  advancedNetworkCapture: false,
  captureMetric: false,
  webVitals: false,
  
  // Console & Debug
  consoleCapture: false,
  debug: false,
  
  // Session Replay
  disableReplay: true,
  sampling: 1,
  recordCanvas: false,
  maskAllInputs: true,
  maskAllText: false,
  blockClass: 'imqa-block',
  ignoreClass: 'imqa-ignore',
  maskClass: 'imqa-mask',
  
  // Storage
  sessionStorage: 'cookie',
  
  // Network
  websocket: true,
  socketio: false,
  disablePreflight: false,
  exporterProtocol: 'json',
  tracePropagationTargets: '',
  ignoreUrls: '',
  
  // Screen Name
  screenNameType: 'routeOnly',
  urlWithSearchParams: false,
  
  // Instrumentations
  instrumentations: {
    console: true,
    document: true,
    errors: true,
    fetch: true,
    interactions: true,
    longtask: true,
    visibility: true,
    connectivity: true,
    postload: true,
    socketio: false,
    websocket: true,
    webvitals: false,
    xhr: true,
  },
  
  // Custom attributes
  defaultAttributes: '{}',
};

export function AdvancedConfigForm() {
  const { config: contextConfig, updateConfig, initIMQA, deinitIMQA, isInitialized, resetToDefaults, clearLocalStorage } = useIMQA();
  const { toast } = useToast();
  const [config, setConfig] = useState<AdvancedConfig>(defaultConfig);

  // Load config from localStorage on mount and watch for context config changes
  useEffect(() => {
    // Load basic config from imqaConfig (used by basic config form)
    const basicConfig = localStorage.getItem('imqaConfig');
    let basicConfigData: Record<string, unknown> = {};
    if (basicConfig) {
      try {
        basicConfigData = JSON.parse(basicConfig);
      } catch (error) {
        console.error('Failed to parse basic config:', error);
      }
    }

    // Load advanced config from imqa-advanced-config
    const savedAdvancedConfig = localStorage.getItem('imqa-advanced-config');
    let advancedConfigData: Record<string, unknown> = {};
    if (savedAdvancedConfig) {
      try {
        advancedConfigData = JSON.parse(savedAdvancedConfig);
      } catch (error) {
        console.error('Failed to parse advanced config:', error);
      }
    }

    // Merge configs: basic config takes precedence for core fields
    const mergedConfig = {
      ...defaultConfig,
      ...advancedConfigData,
    };

    // Override with basic config values if they exist
    if (basicConfigData.collectorUrl) {
      mergedConfig.collectorUrl = String(basicConfigData.collectorUrl);
    }
    if (basicConfigData.serviceName) {
      mergedConfig.serviceName = String(basicConfigData.serviceName);
    }
    if (basicConfigData.serviceVersion) {
      mergedConfig.serviceVersion = String(basicConfigData.serviceVersion);
    }
    if (basicConfigData.serviceKey) {
      mergedConfig.serviceKey = String(basicConfigData.serviceKey);
    }
    if (basicConfigData.serviceNamespace) {
      mergedConfig.serviceNamespace = String(basicConfigData.serviceNamespace);
    }

    setConfig(mergedConfig);
  }, []);

  // Watch for context config changes (from Basic Config)
  useEffect(() => {
    if (contextConfig.collectorUrl) {
      setConfig(prev => ({ ...prev, collectorUrl: contextConfig.collectorUrl || '' }));
    }
    if (contextConfig.serviceName) {
      setConfig(prev => ({ ...prev, serviceName: contextConfig.serviceName || '' }));
    }
    if (contextConfig.serviceVersion) {
      setConfig(prev => ({ ...prev, serviceVersion: contextConfig.serviceVersion || '' }));
    }
    if (contextConfig.serviceKey) {
      setConfig(prev => ({ ...prev, serviceKey: contextConfig.serviceKey || '' }));
    }
  }, [contextConfig.collectorUrl, contextConfig.serviceName, contextConfig.serviceVersion, contextConfig.serviceKey]);

  const handleInputChange = (field: keyof AdvancedConfig, value: string | number | boolean) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleInstrumentationChange = (field: keyof AdvancedConfig['instrumentations'], value: boolean) => {
    setConfig(prev => ({
      ...prev,
      instrumentations: { ...prev.instrumentations, [field]: value }
    }));
  };

  const handleSaveConfig = () => {
    try {
      // Validate JSON fields
      if (config.defaultAttributes) {
        JSON.parse(config.defaultAttributes);
      }
      
      // Save to localStorage
      localStorage.setItem('imqa-advanced-config', JSON.stringify(config));
      
      toast({
        title: "Configuration saved",
        description: "Advanced configuration has been saved to localStorage",
      });
    } catch {
      toast({
        title: "Invalid configuration",
        description: "Please check your JSON syntax in custom attributes",
        variant: "destructive",
      });
    }
  };

  const handleApplyConfig = () => {
    try {
      // Parse arrays and objects
      const tracePropagationTargets = config.tracePropagationTargets
        ? config.tracePropagationTargets.split(',').map(s => s.trim()).filter(Boolean)
        : undefined;
      
      const ignoreUrls = config.ignoreUrls
        ? config.ignoreUrls.split(',').map(s => s.trim()).filter(Boolean)
        : undefined;
      
      const defaultAttributes = config.defaultAttributes
        ? JSON.parse(config.defaultAttributes)
        : undefined;

      // Build IMQA config
      const imqaConfig: Record<string, unknown> = {
        collectorUrl: config.collectorUrl,
        serviceName: config.serviceName,
        serviceVersion: config.serviceVersion,
        serviceKey: config.serviceKey,
        ...(config.serviceNamespace && { serviceNamespace: config.serviceNamespace }),
        ...(config.authorizationToken && { authorizationToken: config.authorizationToken }),
        ...(config.deploymentEnvironment && { deploymentEnvironment: config.deploymentEnvironment }),
        samplingProbability: config.samplingProbability,
        advancedNetworkCapture: config.advancedNetworkCapture,
        captureMetric: config.captureMetric,
        webVitals: config.webVitals,
        consoleCapture: config.consoleCapture,
        debug: config.debug,
        disableReplay: config.disableReplay,
        sampling: config.sampling,
        recordCanvas: config.recordCanvas,
        maskAllInputs: config.maskAllInputs,
        maskAllText: config.maskAllText,
        blockClass: config.blockClass,
        ignoreClass: config.ignoreClass,
        maskClass: config.maskClass,
        sessionStorage: config.sessionStorage,
        websocket: config.websocket,
        socketio: config.socketio,
        disablePreflight: config.disablePreflight,
        exporterProtocol: config.exporterProtocol,
        ...(tracePropagationTargets && { tracePropagationTargets }),
        ...(ignoreUrls && { ignoreUrls }),
        screenNameOption: {
          screenNameType: config.screenNameType,
          urlWithSearchParams: config.urlWithSearchParams,
        },
        instrumentations: config.instrumentations,
        ...(defaultAttributes && { defaultAttributes }),
      };

      // Update config and reinitialize if needed
      updateConfig(imqaConfig);
      
      // Also update the basic config (imqaConfig) with core settings
      const basicConfigData = {
        collectorUrl: config.collectorUrl,
        serviceName: config.serviceName,
        serviceVersion: config.serviceVersion,
        serviceKey: config.serviceKey,
        ...(config.serviceNamespace && { serviceNamespace: config.serviceNamespace }),
      };
      localStorage.setItem('imqaConfig', JSON.stringify(basicConfigData));
      
      if (isInitialized) {
        deinitIMQA();
        setTimeout(() => initIMQA(), 100);
      }

      toast({
        title: "Configuration applied",
        description: "IMQA Web Agent has been configured with new settings",
      });
      } catch (error) {
        toast({
          title: "Configuration error",
          description: `Failed to apply configuration: ${error instanceof Error ? error.message : 'Unknown error'}`,
          variant: "destructive",
        });
      }
  };

  const handleToggleAgent = () => {
    if (isInitialized) {
      deinitIMQA();
      toast({
        title: "IMQA Web Agent deactivated",
        description: "The agent has been stopped successfully",
      });
    } else {
      if (!config.collectorUrl || !config.serviceName || !config.serviceVersion || !config.serviceKey) {
        toast({
          title: "Cannot activate",
          description: "Core configuration fields are required to initialize IMQA Web Agent",
          variant: "destructive",
        });
        return;
      }
      
      handleApplyConfig();
      initIMQA();
      toast({
        title: "IMQA Web Agent activated",
        description: "The agent has been started successfully",
      });
    }
  };

  const handleResetConfig = () => {
    setConfig(defaultConfig);
    localStorage.removeItem('imqa-advanced-config');
    toast({
      title: "Configuration reset",
      description: "All settings have been reset to default values",
    });
  };

  const handleResetToDefaults = () => {
    resetToDefaults();
    setConfig(defaultConfig);
    localStorage.removeItem('imqa-advanced-config');
    toast({
      title: "Reset to defaults",
      description: "Configuration has been reset to DEFAULT_IMQA_CONFIG values and localStorage cleared",
    });
  };

  const handleClearLocalStorage = () => {
    clearLocalStorage();
    localStorage.removeItem('imqa-advanced-config');
    toast({
      title: "localStorage cleared",
      description: "All IMQA configuration has been removed from localStorage",
    });
  };

  // Check if current config differs from DEFAULT_IMQA_CONFIG
  const isModifiedFromDefaults = (): boolean => {
    // Compare core fields
    if (config.collectorUrl !== DEFAULT_IMQA_CONFIG.collectorUrl) return true;
    if (config.serviceName !== DEFAULT_IMQA_CONFIG.serviceName) return true;
    if (config.serviceVersion !== DEFAULT_IMQA_CONFIG.serviceVersion) return true;
    if (config.serviceKey !== DEFAULT_IMQA_CONFIG.serviceKey) return true;
    if (config.serviceNamespace !== DEFAULT_IMQA_CONFIG.serviceNamespace) return true;
    if (config.authorizationToken !== DEFAULT_IMQA_CONFIG.authorizationToken) return true;
    if (config.deploymentEnvironment !== DEFAULT_IMQA_CONFIG.deploymentEnvironment) return true;

    // Compare boolean/number fields
    if (config.samplingProbability !== DEFAULT_IMQA_CONFIG.samplingProbability) return true;
    if (config.advancedNetworkCapture !== DEFAULT_IMQA_CONFIG.advancedNetworkCapture) return true;
    if (config.captureMetric !== DEFAULT_IMQA_CONFIG.captureMetrics) return true;
    if (config.webVitals !== DEFAULT_IMQA_CONFIG.webVitals) return true;
    if (config.consoleCapture !== DEFAULT_IMQA_CONFIG.consoleCapture) return true;
    if (config.debug !== DEFAULT_IMQA_CONFIG.debug) return true;
    if (config.disableReplay !== DEFAULT_IMQA_CONFIG.disableReplay) return true;
    if (config.sampling !== 1) return true; // DEFAULT_IMQA_CONFIG doesn't have sampling field
    if (config.recordCanvas !== DEFAULT_IMQA_CONFIG.recordCanvas) return true;
    if (config.maskAllInputs !== DEFAULT_IMQA_CONFIG.maskAllInputs) return true;
    if (config.maskAllText !== DEFAULT_IMQA_CONFIG.maskAllText) return true;
    if (config.sessionStorage !== DEFAULT_IMQA_CONFIG.sessionStorage) return true;
    if (config.websocket !== DEFAULT_IMQA_CONFIG.websocket) return true;
    if (config.socketio !== DEFAULT_IMQA_CONFIG.socketio) return true;
    if (config.disablePreflight !== DEFAULT_IMQA_CONFIG.disablePreflight) return true;
    if (config.exporterProtocol !== DEFAULT_IMQA_CONFIG.exporterProtocol) return true;

    return false;
  };

  const handleExportConfig = () => {
    const configJson = JSON.stringify(config, null, 2);
    const blob = new Blob([configJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'imqa-config.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Configuration exported",
      description: "Configuration has been downloaded as JSON file",
    });
  };

  const handleImportConfig = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const imported = JSON.parse(e.target?.result as string);
        setConfig({ ...defaultConfig, ...imported });
        toast({
          title: "Configuration imported",
          description: "Configuration has been loaded from file",
        });
      } catch {
        toast({
          title: "Import failed",
          description: "Invalid JSON file format",
          variant: "destructive",
        });
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-2 flex-wrap items-center">
        {isModifiedFromDefaults() && (
          <Badge variant="secondary" className="w-full sm:w-auto mb-2 sm:mb-0 sm:mr-2">
            Modified from defaults
          </Badge>
        )}
        <Button onClick={handleSaveConfig} className="flex-1 sm:flex-none min-w-[120px]">Save Configuration</Button>
        <Button onClick={handleApplyConfig} variant="secondary" className="flex-1 sm:flex-none min-w-[120px]">Apply Configuration</Button>
        <Button
          onClick={handleToggleAgent}
          variant={isInitialized ? "destructive" : "default"}
          className="flex-1 sm:flex-none min-w-[120px]"
        >
          {isInitialized ? "Deactivate Agent" : "Activate Agent"}
        </Button>
        <Button onClick={handleResetConfig} variant="outline" className="flex-1 sm:flex-none min-w-[120px]">Reset to Default</Button>
        <Button onClick={handleResetToDefaults} variant="destructive" className="flex-1 sm:flex-none min-w-[120px]">
          Reset to Defaults
        </Button>
        <Button onClick={handleClearLocalStorage} variant="destructive" className="flex-1 sm:flex-none min-w-[120px]">
          Clear localStorage
        </Button>
        <Button onClick={handleExportConfig} variant="outline" className="flex-1 sm:flex-none min-w-[120px]">Export Config</Button>
        <div className="flex-1 sm:flex-none">
          <input
            type="file"
            accept=".json"
            onChange={handleImportConfig}
            style={{ display: 'none' }}
            id="import-config"
          />
          <Button
            onClick={() => document.getElementById('import-config')?.click()}
            variant="outline"
            className="w-full sm:w-auto min-w-[120px]"
          >
            Import Config
          </Button>
        </div>
      </div>

      <Tabs defaultValue="core" className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <TabsTrigger value="core">Core</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="replay">Session Replay</TabsTrigger>
          <TabsTrigger value="network">Network</TabsTrigger>
          <TabsTrigger value="instrumentations">Instrumentations</TabsTrigger>
          <TabsTrigger value="advanced">Advanced</TabsTrigger>
        </TabsList>

        <TabsContent value="core" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Core Configuration</CardTitle>
              <CardDescription>
                Essential settings for IMQA Web Agent. Values are loaded from localStorage (Basic Config settings).
                Default values will show as placeholders when no saved configuration exists.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="collectorUrl">Collector URL *</Label>
                <Input
                  id="collectorUrl"
                  value={config.collectorUrl}
                  onChange={(e) => handleInputChange('collectorUrl', e.target.value)}
                  placeholder="https://in-otel.imqa.io"
                />
                {!config.collectorUrl && (
                  <p className="text-sm text-muted-foreground">
                    Default: https://in-otel.imqa.io (shown when no saved value exists)
                  </p>
                )}
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="serviceName">Service Name *</Label>
                <Input
                  id="serviceName"
                  value={config.serviceName}
                  onChange={(e) => handleInputChange('serviceName', e.target.value)}
                  placeholder="my-web-service"
                />
                {!config.serviceName && (
                  <p className="text-sm text-muted-foreground">
                    Default: my-web-service (shown when no saved value exists)
                  </p>
                )}
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="serviceVersion">Service Version *</Label>
                <Input
                  id="serviceVersion"
                  value={config.serviceVersion}
                  onChange={(e) => handleInputChange('serviceVersion', e.target.value)}
                  placeholder="1.0.0"
                />
                {!config.serviceVersion && (
                  <p className="text-sm text-muted-foreground">
                    Default: 1.0.0 (shown when no saved value exists)
                  </p>
                )}
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="serviceKey">Service Key *</Label>
                <Input
                  id="serviceKey"
                  value={config.serviceKey}
                  onChange={(e) => handleInputChange('serviceKey', e.target.value)}
                  placeholder="Your IMQA service key"
                  type="password"
                />
                {!config.serviceKey && (
                  <p className="text-sm text-muted-foreground">
                    Default: Your IMQA service key (shown when no saved value exists)
                  </p>
                )}
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="serviceNamespace">Service Namespace</Label>
                <Input
                  id="serviceNamespace"
                  value={config.serviceNamespace}
                  onChange={(e) => handleInputChange('serviceNamespace', e.target.value)}
                  placeholder="Optional service namespace (e.g., my-org)"
                />
                <p className="text-sm text-muted-foreground">
                  Used to group related services together in the IMQA dashboard
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="authorizationToken">Authorization Token</Label>
                <Input
                  id="authorizationToken"
                  value={config.authorizationToken}
                  onChange={(e) => handleInputChange('authorizationToken', e.target.value)}
                  placeholder="Optional authorization token"
                  type="password"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="deploymentEnvironment">Deployment Environment</Label>
                <Select 
                  value={config.deploymentEnvironment} 
                  onValueChange={(value) => handleInputChange('deploymentEnvironment', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="development">Development</SelectItem>
                    <SelectItem value="staging">Staging</SelectItem>
                    <SelectItem value="production">Production</SelectItem>
                    <SelectItem value="test">Test</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Performance & Sampling</CardTitle>
              <CardDescription>Configure sampling and performance monitoring</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="samplingProbability">Sampling Probability (0-1)</Label>
                <Input
                  id="samplingProbability"
                  type="number"
                  min="0"
                  max="1"
                  step="0.1"
                  value={config.samplingProbability}
                  onChange={(e) => handleInputChange('samplingProbability', parseFloat(e.target.value))}
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="advancedNetworkCapture"
                  checked={config.advancedNetworkCapture}
                  onCheckedChange={(checked) => handleInputChange('advancedNetworkCapture', checked)}
                />
                <Label htmlFor="advancedNetworkCapture">Advanced Network Capture</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="captureMetric"
                  checked={config.captureMetric}
                  onCheckedChange={(checked) => handleInputChange('captureMetric', checked)}
                />
                <Label htmlFor="captureMetric">Capture Metrics</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="webVitals"
                  checked={config.webVitals}
                  onCheckedChange={(checked) => handleInputChange('webVitals', checked)}
                />
                <Label htmlFor="webVitals">Web Vitals</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="consoleCapture"
                  checked={config.consoleCapture}
                  onCheckedChange={(checked) => handleInputChange('consoleCapture', checked)}
                />
                <Label htmlFor="consoleCapture">Console Capture</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="debug"
                  checked={config.debug}
                  onCheckedChange={(checked) => handleInputChange('debug', checked)}
                />
                <Label htmlFor="debug">Debug Mode</Label>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="replay" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Session Replay Settings</CardTitle>
              <CardDescription>Configure session recording and privacy settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="disableReplay"
                  checked={config.disableReplay}
                  onCheckedChange={(checked) => handleInputChange('disableReplay', checked)}
                />
                <Label htmlFor="disableReplay">Disable Session Replay</Label>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="sampling">Session Sampling (0-1)</Label>
                <Input
                  id="sampling"
                  type="number"
                  min="0"
                  max="1"
                  step="0.1"
                  value={config.sampling}
                  onChange={(e) => handleInputChange('sampling', parseFloat(e.target.value))}
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="recordCanvas"
                  checked={config.recordCanvas}
                  onCheckedChange={(checked) => handleInputChange('recordCanvas', checked)}
                />
                <Label htmlFor="recordCanvas">Record Canvas</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="maskAllInputs"
                  checked={config.maskAllInputs}
                  onCheckedChange={(checked) => handleInputChange('maskAllInputs', checked)}
                />
                <Label htmlFor="maskAllInputs">Mask All Inputs</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="maskAllText"
                  checked={config.maskAllText}
                  onCheckedChange={(checked) => handleInputChange('maskAllText', checked)}
                />
                <Label htmlFor="maskAllText">Mask All Text</Label>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="blockClass">Block Class</Label>
                <Input
                  id="blockClass"
                  value={config.blockClass}
                  onChange={(e) => handleInputChange('blockClass', e.target.value)}
                  placeholder="CSS class to block"
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="ignoreClass">Ignore Class</Label>
                <Input
                  id="ignoreClass"
                  value={config.ignoreClass}
                  onChange={(e) => handleInputChange('ignoreClass', e.target.value)}
                  placeholder="CSS class to ignore"
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="maskClass">Mask Class</Label>
                <Input
                  id="maskClass"
                  value={config.maskClass}
                  onChange={(e) => handleInputChange('maskClass', e.target.value)}
                  placeholder="CSS class to mask"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="network" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Network Configuration</CardTitle>
              <CardDescription>Configure network monitoring and protocols</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="sessionStorage">Session Storage Type</Label>
                <Select 
                  value={config.sessionStorage} 
                  onValueChange={(value: 'cookie' | 'localStorage') => handleInputChange('sessionStorage', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cookie">Cookie</SelectItem>
                    <SelectItem value="localStorage">Local Storage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="websocket"
                  checked={config.websocket}
                  onCheckedChange={(checked) => handleInputChange('websocket', checked)}
                />
                <Label htmlFor="websocket">WebSocket Monitoring</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="socketio"
                  checked={config.socketio}
                  onCheckedChange={(checked) => handleInputChange('socketio', checked)}
                />
                <Label htmlFor="socketio">Socket.IO Monitoring</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="disablePreflight"
                  checked={config.disablePreflight}
                  onCheckedChange={(checked) => handleInputChange('disablePreflight', checked)}
                />
                <Label htmlFor="disablePreflight">Disable Preflight Requests</Label>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="exporterProtocol">Exporter Protocol</Label>
                <Select 
                  value={config.exporterProtocol} 
                  onValueChange={(value: 'json' | 'proto') => handleInputChange('exporterProtocol', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="json">JSON</SelectItem>
                    <SelectItem value="proto">Protocol Buffers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="tracePropagationTargets">Trace Propagation Targets (comma-separated)</Label>
                <Textarea
                  id="tracePropagationTargets"
                  value={config.tracePropagationTargets}
                  onChange={(e) => handleInputChange('tracePropagationTargets', e.target.value)}
                  placeholder="api.example.com, *.mydomain.com"
                  rows={2}
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="ignoreUrls">Ignore URLs (comma-separated)</Label>
                <Textarea
                  id="ignoreUrls"
                  value={config.ignoreUrls}
                  onChange={(e) => handleInputChange('ignoreUrls', e.target.value)}
                  placeholder="/health, /metrics, *.png"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="instrumentations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Instrumentations</CardTitle>
              <CardDescription>Enable or disable specific monitoring features</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Object.entries(config.instrumentations).map(([key, value]) => (
                  <div key={key} className="flex items-center space-x-2">
                    <Checkbox
                      id={key}
                      checked={value}
                      onCheckedChange={(checked) => handleInstrumentationChange(key as keyof AdvancedConfig['instrumentations'], checked as boolean)}
                    />
                    <Label htmlFor={key} className="capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </Label>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="advanced" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Advanced Settings</CardTitle>
              <CardDescription>Screen naming and custom attributes</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="screenNameType">Screen Name Type</Label>
                <Select 
                  value={config.screenNameType} 
                  onValueChange={(value: 'routeOnly' | 'titleOnly' | 'full') => handleInputChange('screenNameType', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="routeOnly">Route Only</SelectItem>
                    <SelectItem value="titleOnly">Title Only</SelectItem>
                    <SelectItem value="full">Full (Title + Route)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="urlWithSearchParams"
                  checked={config.urlWithSearchParams}
                  onCheckedChange={(checked) => handleInputChange('urlWithSearchParams', checked)}
                />
                <Label htmlFor="urlWithSearchParams">Include URL Search Parameters</Label>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="defaultAttributes">Default Attributes (JSON)</Label>
                <Textarea
                  id="defaultAttributes"
                  value={config.defaultAttributes}
                  onChange={(e) => handleInputChange('defaultAttributes', e.target.value)}
                  placeholder='{"customKey": "customValue", "environment": "test"}'
                  rows={4}
                  className="font-mono text-sm"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
